/**
 * O cinto de ferramentas e a maquina de estados.
 *
 * `Tool.apply` e PURA: recebe um snapshot e as escolhas, devolve um snapshot novo.
 * Nao toca no DOM, e por isso e testavel sem navegador. A maquina de estados
 * (idle -> escolhendo(i) -> previa -> comita) mora na interface.
 *
 * Detalhe com consequencia psicometrica: dar `angle-bisector` como FERRAMENTA torna
 * um item de bissecao trivial. Entao o template controla a dificuldade exatamente por
 * QUAIS ferramentas coloca no cinto -- e essa e a fonte de dificuldade
 * `construction.free-tools`.
 */

import type { Vec2 } from './vec.ts';
import type { AnyDef, ConstructionSnapshot, GeoId, GeoObject } from './construction.ts';
import { addObject, dependenciesOf, evaluate, freshId, removeObject } from './construction.ts';
import type { SnapResult } from './snapping.ts';

export type ToolId =
  | 'select'
  | 'point'
  | 'segment'
  | 'ray'
  | 'line'
  | 'circle-center-through'
  | 'compass'
  | 'intersect'
  | 'midpoint'
  | 'perpendicular'
  | 'parallel'
  | 'perp-bisector'
  | 'angle-bisector'
  | 'reflect'
  | 'undo'
  | 'delete';

/** Ferramentas que efetivamente criam objetos (as demais sao de controle). */
export type CreatingToolId = Exclude<ToolId, 'select' | 'undo' | 'delete'>;

export interface PickRef {
  /** Objeto clicado, quando a ferramenta pede um objeto (reta, circulo). */
  readonly id: GeoId | null;
  readonly at: Vec2;
  readonly snap: SnapResult;
}

export type ToolAction =
  | {
      readonly seq: number;
      readonly tMs: number;
      readonly tool: CreatingToolId;
      readonly picks: readonly PickRef[];
      readonly created: readonly GeoId[];
    }
  | { readonly seq: number; readonly tMs: number; readonly tool: 'undo'; readonly toSeq: number }
  | { readonly seq: number; readonly tMs: number; readonly tool: 'delete'; readonly target: GeoId };

export type PickKind = 'any' | 'point' | 'line' | 'circle' | 'line-or-circle';

export interface Tool {
  readonly id: ToolId;
  readonly label: string;
  /** Glifo simples, desenhado no botao. */
  readonly icon: string;
  readonly arity: number;
  /** O que cada escolha aceita, em ordem. */
  readonly picks: readonly PickKind[];
  /** Instrucao mostrada a cada passo. */
  readonly prompt: readonly string[];
  readonly apply: (snap: ConstructionSnapshot, picks: readonly PickRef[]) => ApplyResult;
}

export interface ApplyResult {
  readonly snapshot: ConstructionSnapshot;
  readonly created: readonly GeoId[];
}

const noop = (snapshot: ConstructionSnapshot): ApplyResult => ({ snapshot, created: [] });

// ---------------------------------------------------------------------------
// Materializacao de pontos a partir da proveniencia do snap
// ---------------------------------------------------------------------------

/**
 * Garante que existe um ponto no local escolhido, criando-o com a definicao que a
 * proveniencia do snap determina.
 *
 * E AQUI que "construiu" e "chutou" se separam: um clique sobre uma intersecao vira
 * um ponto derivado; um clique no vazio vira `free`.
 */
export function ensurePoint(
  snap: ConstructionSnapshot,
  pick: PickRef,
): { snapshot: ConstructionSnapshot; id: GeoId; createdId: GeoId | null } {
  const s = pick.snap;

  if (s.to === 'point') {
    return { snapshot: snap, id: s.id, createdId: null };
  }

  const id = freshId(snap, 'P');
  let def: AnyDef;
  switch (s.to) {
    case 'intersection':
      def = { via: 'intersection', a: s.a, b: s.b, branch: s.branch };
      break;
    case 'object':
      def = { via: 'on-object', host: s.host, t: s.t };
      break;
    case 'lattice':
      def = { via: 'lattice', i: s.i, j: s.j, step: latticeStepOf(s.at, s.i, s.j) };
      break;
    case 'none':
      def = { via: 'free', at: s.at };
      break;
  }

  return {
    snapshot: addObject(snap, { id, kind: 'point', def }),
    id,
    createdId: id,
  };
}

function latticeStepOf(at: Vec2, i: number, j: number): number {
  if (i !== 0) return at.x / i;
  if (j !== 0) return at.y / j;
  return 1;
}

/** Materializa varias escolhas como pontos, acumulando o snapshot. */
function ensurePoints(
  snap: ConstructionSnapshot,
  picks: readonly PickRef[],
): { snapshot: ConstructionSnapshot; ids: GeoId[]; created: GeoId[] } {
  let cur = snap;
  const ids: GeoId[] = [];
  const created: GeoId[] = [];
  for (const pk of picks) {
    const r = ensurePoint(cur, pk);
    cur = r.snapshot;
    ids.push(r.id);
    if (r.createdId) created.push(r.createdId);
  }
  return { snapshot: cur, ids, created };
}

// ---------------------------------------------------------------------------
// Definicoes das ferramentas
// ---------------------------------------------------------------------------

function twoPointLine(
  span: 'segment' | 'ray' | 'line',
  label: string,
  icon: string,
  id: ToolId,
): Tool {
  return {
    id,
    label,
    icon,
    arity: 2,
    picks: ['any', 'any'],
    prompt:
      span === 'ray'
        ? ['Escolha a origem', 'Escolha um ponto por onde passa']
        : ['Escolha o primeiro ponto', 'Escolha o segundo ponto'],
    apply(snap, picks) {
      const { snapshot, ids, created } = ensurePoints(snap, picks);
      const a = ids[0];
      const b = ids[1];
      if (!a || !b || a === b) return { snapshot, created };
      const lid = freshId(snapshot, 'r');
      return {
        snapshot: addObject(snapshot, {
          id: lid,
          kind: 'line',
          def: { via: 'through', a, b, span },
        }),
        created: [...created, lid],
      };
    },
  };
}

export const TOOLS: Readonly<Record<ToolId, Tool>> = Object.freeze({
  select: {
    id: 'select',
    label: 'Selecionar',
    icon: 'cursor',
    arity: 0,
    picks: [],
    prompt: [],
    apply: noop,
  },

  undo: {
    id: 'undo',
    label: 'Desfazer',
    icon: 'undo',
    arity: 0,
    picks: [],
    prompt: [],
    apply: noop,
  },

  delete: {
    id: 'delete',
    label: 'Apagar',
    icon: 'trash',
    arity: 1,
    picks: ['any'],
    prompt: ['Escolha o que apagar'],
    apply: noop,
  },

  point: {
    id: 'point',
    label: 'Ponto',
    icon: 'point',
    arity: 1,
    picks: ['any'],
    prompt: ['Marque um ponto'],
    apply(snap, picks) {
      const pk = picks[0];
      if (!pk) return noop(snap);
      const r = ensurePoint(snap, pk);
      return { snapshot: r.snapshot, created: r.createdId ? [r.createdId] : [] };
    },
  },

  segment: twoPointLine('segment', 'Segmento', 'segment', 'segment'),
  ray: twoPointLine('ray', 'Semirreta', 'ray', 'ray'),
  line: twoPointLine('line', 'Reta', 'line', 'line'),

  'circle-center-through': {
    id: 'circle-center-through',
    label: 'Circunferência',
    icon: 'circle',
    arity: 2,
    picks: ['any', 'any'],
    prompt: ['Escolha o centro', 'Escolha um ponto da circunferência'],
    apply(snap, picks) {
      const { snapshot, ids, created } = ensurePoints(snap, picks);
      const center = ids[0];
      const through = ids[1];
      if (!center || !through || center === through) return { snapshot, created };
      const kid = freshId(snapshot, 'k');
      return {
        snapshot: addObject(snapshot, {
          id: kid,
          kind: 'circle',
          def: { via: 'center-through', center, through },
        }),
        created: [...created, kid],
      };
    },
  },

  compass: {
    id: 'compass',
    label: 'Compasso',
    icon: 'compass',
    arity: 3,
    picks: ['any', 'any', 'any'],
    prompt: ['Escolha o centro', 'Abertura: primeiro ponto', 'Abertura: segundo ponto'],
    apply(snap, picks) {
      const { snapshot, ids, created } = ensurePoints(snap, picks);
      const center = ids[0];
      const radiusA = ids[1];
      const radiusB = ids[2];
      if (!center || !radiusA || !radiusB || radiusA === radiusB) return { snapshot, created };
      const kid = freshId(snapshot, 'k');
      return {
        snapshot: addObject(snapshot, {
          id: kid,
          kind: 'circle',
          def: { via: 'compass', center, radiusA, radiusB },
        }),
        created: [...created, kid],
      };
    },
  },

  intersect: {
    id: 'intersect',
    label: 'Interseção',
    icon: 'intersect',
    arity: 2,
    picks: ['line-or-circle', 'line-or-circle'],
    prompt: ['Escolha o primeiro objeto', 'Escolha o segundo objeto'],
    apply(snap, picks) {
      const a = picks[0]?.id;
      const b = picks[1]?.id;
      if (!a || !b || a === b) return noop(snap);

      // Cria os dois ramos quando existem: e o comportamento que o aluno espera, e
      // evita o problema de "qual dos dois voce queria?".
      const { objects } = evaluate(snap);
      const oa = objects.get(a);
      const ob = objects.get(b);
      if (!oa || !ob) return noop(snap);

      const nBranches = branchCount(oa, ob);
      let cur = snap;
      const created: GeoId[] = [];
      for (let k = 0; k < nBranches; k++) {
        const id = freshId(cur, 'P');
        cur = addObject(cur, {
          id,
          kind: 'point',
          def: { via: 'intersection', a, b, branch: k as 0 | 1 },
        });
        created.push(id);
      }
      return { snapshot: cur, created };
    },
  },

  midpoint: {
    id: 'midpoint',
    label: 'Ponto médio',
    icon: 'midpoint',
    arity: 2,
    picks: ['any', 'any'],
    prompt: ['Primeiro ponto', 'Segundo ponto'],
    apply(snap, picks) {
      const { snapshot, ids, created } = ensurePoints(snap, picks);
      const a = ids[0];
      const b = ids[1];
      if (!a || !b || a === b) return { snapshot, created };
      const id = freshId(snapshot, 'M');
      return {
        snapshot: addObject(snapshot, { id, kind: 'point', def: { via: 'midpoint', a, b } }),
        created: [...created, id],
      };
    },
  },

  perpendicular: {
    id: 'perpendicular',
    label: 'Perpendicular',
    icon: 'perp',
    arity: 2,
    picks: ['any', 'line'],
    prompt: ['Escolha o ponto', 'Escolha a reta'],
    apply(snap, picks) {
      const pk = picks[0];
      const lineId = picks[1]?.id;
      if (!pk || !lineId) return noop(snap);
      const r = ensurePoint(snap, pk);
      const id = freshId(r.snapshot, 'r');
      return {
        snapshot: addObject(r.snapshot, {
          id,
          kind: 'line',
          def: { via: 'perpendicular', through: r.id, to: lineId },
        }),
        created: r.createdId ? [r.createdId, id] : [id],
      };
    },
  },

  parallel: {
    id: 'parallel',
    label: 'Paralela',
    icon: 'parallel',
    arity: 2,
    picks: ['any', 'line'],
    prompt: ['Escolha o ponto', 'Escolha a reta'],
    apply(snap, picks) {
      const pk = picks[0];
      const lineId = picks[1]?.id;
      if (!pk || !lineId) return noop(snap);
      const r = ensurePoint(snap, pk);
      const id = freshId(r.snapshot, 'r');
      return {
        snapshot: addObject(r.snapshot, {
          id,
          kind: 'line',
          def: { via: 'parallel', through: r.id, to: lineId },
        }),
        created: r.createdId ? [r.createdId, id] : [id],
      };
    },
  },

  'perp-bisector': {
    id: 'perp-bisector',
    label: 'Mediatriz',
    icon: 'perpbis',
    arity: 2,
    picks: ['any', 'any'],
    prompt: ['Primeiro ponto', 'Segundo ponto'],
    apply(snap, picks) {
      const { snapshot, ids, created } = ensurePoints(snap, picks);
      const a = ids[0];
      const b = ids[1];
      if (!a || !b || a === b) return { snapshot, created };
      const id = freshId(snapshot, 'r');
      return {
        snapshot: addObject(snapshot, { id, kind: 'line', def: { via: 'perp-bisector', a, b } }),
        created: [...created, id],
      };
    },
  },

  'angle-bisector': {
    id: 'angle-bisector',
    label: 'Bissetriz',
    icon: 'bisector',
    arity: 3,
    picks: ['any', 'any', 'any'],
    prompt: ['Ponto do primeiro lado', 'Vértice', 'Ponto do segundo lado'],
    apply(snap, picks) {
      const { snapshot, ids, created } = ensurePoints(snap, picks);
      const a = ids[0];
      const vertex = ids[1];
      const b = ids[2];
      if (!a || !vertex || !b) return { snapshot, created };
      const id = freshId(snapshot, 'r');
      return {
        snapshot: addObject(snapshot, {
          id,
          kind: 'line',
          def: { via: 'bisector', a, vertex, b },
        }),
        created: [...created, id],
      };
    },
  },

  reflect: {
    id: 'reflect',
    label: 'Refletir',
    icon: 'reflect',
    arity: 2,
    picks: ['any', 'line'],
    prompt: ['Escolha o ponto', 'Escolha o eixo'],
    apply(snap, picks) {
      const pk = picks[0];
      const mirror = picks[1]?.id;
      if (!pk || !mirror) return noop(snap);
      const r = ensurePoint(snap, pk);
      const id = freshId(r.snapshot, 'P');
      return {
        snapshot: addObject(r.snapshot, {
          id,
          kind: 'point',
          def: { via: 'reflect', p: r.id, mirror },
        }),
        created: r.createdId ? [r.createdId, id] : [id],
      };
    },
  },
});

function branchCount(a: GeoObject, b: GeoObject): number {
  if (a.kind === 'line' && b.kind === 'line') return 1;
  if (a.kind === 'point' || b.kind === 'point') return 0;
  return 2;
}

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------

/**
 * Reexecuta uma trajetoria sobre os dados do item.
 *
 * O CORRETOR USA ISTO, NAO O SNAPSHOT DO CLIENTE. O snapshot que chega do navegador
 * e apenas cache e nao e confiavel; a trajetoria e a resposta de verdade. Como
 * consequencia util, o servidor consegue recorrigir tudo so a partir da telemetria.
 */
export function replay(
  givens: ConstructionSnapshot,
  actions: readonly ToolAction[],
): ConstructionSnapshot {
  return reexecutar(givens, actions).snapshot;
}

/**
 * A reexecução, com a origem de cada objeto: qual ação o criou.
 *
 * DESFAZER É PILHA, como na prancheta: `toSeq` é a posição, na pilha de estados, do
 * estado a que se volta — e o que estava acima dele sai da pilha. Uma versão anterior
 * guardava um registro que só crescia, e numa sequência "desfazer, criar, criar,
 * desfazer" o segundo desfazer voltava, na correção, a um estado diferente do que o
 * aluno viu na tela.
 */
/** De que ação veio cada objeto, e se ele é o RESULTADO dela ou um ponto de passagem. */
interface Origem {
  readonly acao: number;
  readonly principal: boolean;
}

function reexecutar(
  givens: ConstructionSnapshot,
  actions: readonly ToolAction[],
): { snapshot: ConstructionSnapshot; origem: ReadonlyMap<string, Origem> } {
  type Estado = { snap: ConstructionSnapshot; origem: Map<string, Origem> };
  const pilha: Estado[] = [{ snap: givens, origem: new Map() }];
  actions.forEach((act, k) => {
    const topo = pilha[pilha.length - 1] as Estado;
    if (act.tool === 'undo') {
      const alvo = Math.max(0, Math.min(act.toSeq, pilha.length - 1));
      pilha.length = alvo + 1;
      return;
    }
    if (act.tool === 'delete') {
      pilha.push({ snap: removeObject(topo.snap, act.target), origem: topo.origem });
      return;
    }
    const r = TOOLS[act.tool].apply(topo.snap, act.picks);
    const origem = new Map(topo.origem);
    // O resultado da ação é o último objeto criado (a circunferência, a reta, o ponto,
    // o ponto médio); os anteriores são pontos de passagem, criados ao clicar no vazio
    // ou sobre um objeto. A interseção pode dar dois pontos, e os dois são resultado.
    const principais = new Set(
      (act.tool === 'intersect' ? r.created : r.created.slice(-1)).map((id) => id as string),
    );
    for (const id of r.created)
      origem.set(id as string, { acao: k, principal: principais.has(id as string) });
    pilha.push({ snap: r.snapshot, origem });
  });
  const fim = pilha[pilha.length - 1] as Estado;
  return { snapshot: fim.snap, origem: fim.origem };
}

/** Quantas acoes de criacao a trajetoria tem, desfeitas ou nao. Ver `movimentosEfetivos`. */
export function moveCount(actions: readonly ToolAction[]): number {
  return actions.filter((a) => a.tool !== 'undo' && a.tool !== 'delete').length;
}

/**
 * MOVIMENTOS QUE CONTAM (a economia, estilo Euclidea): as ações de criação cujo
 * RESULTADO continua na construção final — ou das quais algum ponto de passagem é usado
 * por um objeto que continua.
 *
 * - Desfazer tira o movimento: o objeto sai.
 * - Apagar com a lixeira um objeto que sobrou tira o movimento: foi um passo à toa. Os
 *   pontos que ele criou de passagem (o centro e o ponto de uma circunferência traçada
 *   no vazio) podem ficar na tela; soltos, não contam.
 * - E não dá para "roubar" apagando os passos auxiliares depois de chegar à figura:
 *   apagar é em CASCATA (`removeObject`), e tudo o que depende do objeto apagado sai
 *   junto. Apagar um arco de que a bissetriz depende apaga a bissetriz — e a
 *   construção deixa de estar certa.
 * - Marcar um ponto num CRUZAMENTO não é movimento: como no Euclidea, o cruzamento de
 *   dois objetos traçados já é um ponto — e a correção o reconhece mesmo sem marcar
 *   (ver `items/_comum/construcao.ts`). Contá-lo faria o aluno perder estrela por
 *   deixar a construção explícita.
 *
 * É esta contagem que a prancheta mostra e que dá as estrelas: a mesma nos dois lugares.
 */
export function movimentosEfetivos(
  givens: ConstructionSnapshot,
  actions: readonly ToolAction[],
): number {
  const { snapshot, origem } = reexecutar(givens, actions);
  const usados = new Set<string>();
  for (const id of snapshot.order) {
    const def = snapshot.defs[id as string];
    if (def) for (const d of dependenciesOf(def)) usados.add(d as string);
  }
  const acoes = new Set<number>();
  for (const id of snapshot.order) {
    const k = id as string;
    const o = origem.get(k);
    if (!o) continue;
    if (snapshot.kinds[k] === 'point' && snapshot.defs[k]?.via === 'intersection') continue;
    if (o.principal || usados.has(k)) acoes.add(o.acao);
  }
  return acoes.size;
}

export function undoCount(actions: readonly ToolAction[]): number {
  return actions.filter((a) => a.tool === 'undo').length;
}
